import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  BenefitEnrolmentView, BenefitPlanView, CompaRatioView, ControlAccountView,
  ElementGlMappingView, PayScaleView, SalaryRevisionView,
} from '../types'

const COMP = '/v1/compensation'
const BEN = '/v1/benefits'

export function usePayScales() {
  return useQuery({
    queryKey: ['compensation', 'pay-scales'],
    queryFn: async () => (await apiClient.get<PayScaleView[]>(`${COMP}/pay-scales`)).data,
  })
}

export function useSalaryRevisions() {
  return useQuery({
    queryKey: ['compensation', 'revisions'],
    queryFn: async () => (await apiClient.get<SalaryRevisionView[]>(`${COMP}/revisions`)).data,
  })
}

export function useEmployeeRevisions(employeeId: string | undefined) {
  return useQuery({
    queryKey: ['compensation', 'revisions', employeeId],
    enabled: Boolean(employeeId),
    queryFn: async () =>
      (await apiClient.get<SalaryRevisionView[]>(`${COMP}/revisions/employee/${employeeId}`)).data,
  })
}

export function useCompaRatio(employeeId: string | undefined, salary: number | undefined) {
  return useQuery({
    queryKey: ['compensation', 'compa-ratio', employeeId, salary],
    enabled: Boolean(employeeId) && salary != null,
    queryFn: async () =>
      (await apiClient.get<CompaRatioView>(`${COMP}/compa-ratio`, {
        params: { employeeId, salary },
      })).data,
  })
}

export function useGlMappings() {
  return useQuery({
    queryKey: ['compensation', 'gl-mappings'],
    queryFn: async () =>
      (await apiClient.get<ElementGlMappingView[]>(`${COMP}/gl-mappings`)).data,
  })
}

export function useControlAccounts() {
  return useQuery({
    queryKey: ['compensation', 'control-accounts'],
    queryFn: async () =>
      (await apiClient.get<ControlAccountView[]>(`${COMP}/control-accounts`)).data,
  })
}

export function useCompensationMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['compensation'] })

  return {
    saveScale: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<PayScaleView>(`${COMP}/pay-scales/${id}`, body)).data
          : (await apiClient.post<PayScaleView>(`${COMP}/pay-scales`, body)).data,
      onSuccess: invalidate,
    }),
    propose: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<SalaryRevisionView>(`${COMP}/revisions`, body)).data,
      onSuccess: invalidate,
    }),
    submit: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<SalaryRevisionView>(`${COMP}/revisions/${id}/submit`)).data,
      onSuccess: invalidate,
    }),
    approve: useMutation({
      mutationFn: async ({ id, comment }: { id: string; comment?: string }) =>
        (await apiClient.post<SalaryRevisionView>(`${COMP}/revisions/${id}/approve`, { comment })).data,
      onSuccess: invalidate,
    }),
    reject: useMutation({
      mutationFn: async ({ id, comment }: { id: string; comment?: string }) =>
        (await apiClient.post<SalaryRevisionView>(`${COMP}/revisions/${id}/reject`, { comment })).data,
      onSuccess: invalidate,
    }),
    applyRevision: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<SalaryRevisionView>(`${COMP}/revisions/${id}/apply`)).data,
      onSuccess: invalidate,
    }),
    saveMapping: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<ElementGlMappingView>(`${COMP}/gl-mappings`, body)).data,
      onSuccess: invalidate,
    }),
    saveControlAccount: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.put<ControlAccountView>(`${COMP}/control-accounts`, body)).data,
      onSuccess: invalidate,
    }),
  }
}

// --- benefits --------------------------------------------------------------

export function useBenefitPlans() {
  return useQuery({
    queryKey: ['benefits', 'plans'],
    queryFn: async () => (await apiClient.get<BenefitPlanView[]>(`${BEN}/plans`)).data,
  })
}

export function useBenefitEnrolments(employeeId: string | undefined) {
  return useQuery({
    queryKey: ['benefits', 'enrolments', employeeId],
    enabled: Boolean(employeeId),
    queryFn: async () =>
      (await apiClient.get<BenefitEnrolmentView[]>(
        `${BEN}/enrolments/employee/${employeeId}`)).data,
  })
}

export function useBenefitMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['benefits'] })

  return {
    savePlan: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<BenefitPlanView>(`${BEN}/plans/${id}`, body)).data
          : (await apiClient.post<BenefitPlanView>(`${BEN}/plans`, body)).data,
      onSuccess: invalidate,
    }),
    /** Retires a plan. Refused while anybody is still enrolled and being charged for it. */
    retirePlan: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.post(`${BEN}/plans/${id}/deactivate`)
      },
      onSuccess: invalidate,
    }),
    enrol: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<BenefitEnrolmentView>(`${BEN}/enrolments`, body)).data,
      onSuccess: invalidate,
    }),
    end: useMutation({
      mutationFn: async ({ id, effectiveTo }: { id: string; effectiveTo: string }) =>
        (await apiClient.post<BenefitEnrolmentView>(
          `${BEN}/enrolments/${id}/end`, { effectiveTo })).data,
      onSuccess: invalidate,
    }),
  }
}
